import { Link, MenuItem, Stack, Typography } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@opengov/react-capital-assets';
import React, { MouseEvent } from 'react';

import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../../enums';
import { BadgeVariant, UtilityNames } from '../../types';

import { NAV_COLOR_PALETTE } from '../../Components/styleConstants';
import { ConditionalBadgeNotificationRenderer } from '../Components/ConditionalBadgeNotificationRenderer';
import { TRAY_BUTTON_ICONS } from '../Components/TrayControllerButton';

interface IMobileUtilityMenuOptionProps {
  label: UtilityNames | string;
  hideChevron?: boolean;
  autoFocus?: boolean;
  customMuiIcon?: React.ElementType;
  stringIcon?: string;
  firstTrayMenuItemRef?: React.MutableRefObject<HTMLLIElement>;
  url?: string;
  openInNewTab?: boolean;
  badgeContent?: number;
  badgeVariant?: BadgeVariant;
  linkComponent?: React.ElementType;
  handleClick(e: MouseEvent<HTMLElement>): void;
}

const styles = {
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: '40px',
    width: '100%',
    px: 3
  }
};

export const MobileUtilityMenuOption: React.FC<IMobileUtilityMenuOptionProps> = ({
  label,
  customMuiIcon,
  stringIcon,
  hideChevron = false,
  autoFocus = false,
  firstTrayMenuItemRef,
  url,
  openInNewTab = false,
  linkComponent,
  handleClick,
  badgeContent,
  badgeVariant
}) => {
  const linkRef = React.useRef<HTMLAnchorElement>(null);
  const UtilityIcon = customMuiIcon || TRAY_BUTTON_ICONS[label];

  // If the customMuiIcon is not a valid MUI Icon component, throw an error
  // It can also be a wrapped react svg component coming from react-capital-assets therefore a function is acceptable
  if (customMuiIcon && typeof UtilityIcon !== 'object' && typeof UtilityIcon !== 'function') {
    console.error('customMuiIcon must be a valid MUI Icon component');
  }

  return (
    <MenuItem
      autoFocus={autoFocus}
      data-og={getDynamicDataOGId(DATA_OG.tray_controller_button, label)}
      data-test={getDynamicDataTestId(DATA_TEST.tray_controller_button, label.toLowerCase())}
      onClick={(e) => {
        if (url && linkRef?.current) {
          linkRef.current.click();
        }
        handleClick(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          // Prevent from bubbling up and triggering handleClose on PopperSubmenuContainer
          e.stopPropagation();
        }
      }}
      sx={styles.menuItem}
      ref={firstTrayMenuItemRef}
    >
      <Stack direction="row" gap={1} alignItems="center">
        <ConditionalBadgeNotificationRenderer
          stringIcon={stringIcon}
          utilityName={label}
          customMuiIcon={UtilityIcon}
          badgeContent={badgeContent}
          badgeVariant={badgeVariant}
        />
        {url && (
          <Link
            href={url}
            target={openInNewTab ? '_blank' : undefined}
            underline="none"
            fontWeight={'semiBold'}
            component={linkComponent || 'a'}
            sx={{
              color: NAV_COLOR_PALETTE.primaryGray,
              ':visited': {
                color: NAV_COLOR_PALETTE.primaryGray
              },
              display: 'block'
            }}
            variant="bodyDefault"
            noWrap={true}
            ref={linkRef}
            tabIndex={-1}
          >
            {label}
          </Link>
        )}
        {!url && (
          <Typography fontWeight="semiBold" component="span" textAlign="center" color={NAV_COLOR_PALETTE.primaryGray}>
            {label}
          </Typography>
        )}
      </Stack>
      {!hideChevron && <ChevronRightIcon sx={{ color: NAV_COLOR_PALETTE.primaryGray }} />}
    </MenuItem>
  );
};
